/**
 * Recovery phiên bị ngắt — Prompt.md §27 (quyết định #15).
 *
 * iOS Safari kill tab nền khá tuỳ ý, và một phiên học 45 phút mất trắng vì trẻ đổi app là lỗi mà
 * người dùng sẽ không tha lần thứ hai. Snapshot đã được ghi mỗi 15 giây — thiếu bước này thì công
 * ghi snapshot vô nghĩa.
 */

import type { SessionRepository } from "@studystraight/session-core";
import type { StudySession } from "@studystraight/shared-types";

export interface RecoverableSession {
  session: StudySession;
  /** ms — snapshot được ghi lúc nào, để UI nói "phiên học lúc 19:30". */
  savedAt: number;
}

/**
 * Phiên dở dang đủ điều kiện để hỏi người dùng.
 *
 * Phiên quá ngắn bị bỏ im lặng: hỏi "có lưu phiên 8 giây không?" mỗi lần mở app là quấy rầy chứ
 * không phải cứu dữ liệu.
 */
export async function findRecoverableSession(
  repository: SessionRepository,
  minDurationMs: number,
): Promise<RecoverableSession | null> {
  const pending = await repository.getPendingSnapshot();
  if (pending === null) return null;

  if (pending.session.duration < minDurationMs) {
    await repository.clearSnapshot();
    return null;
  }
  return { session: pending.session, savedAt: pending.savedAt };
}

/**
 * Người dùng đồng ý lưu.
 *
 * `endTime` lấy `savedAt` (lần ghi snapshot cuối) chứ không lấy giờ hiện tại: phiên đã kết thúc
 * lúc tab bị kill, không phải lúc mở app lại sáng hôm sau.
 */
export async function acceptRecovery(
  repository: SessionRepository,
  recoverable: RecoverableSession,
): Promise<StudySession> {
  const session: StudySession = { ...recoverable.session, endTime: recoverable.savedAt };
  await repository.save(session);
  await repository.clearSnapshot();
  return session;
}

export async function discardRecovery(repository: SessionRepository): Promise<void> {
  await repository.clearSnapshot();
}
